import {
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';

import { INTL_IDS } from './messages';
import { chooseFileSaga, processFileSaga, uploadDocumentJiraSaga } from '../../../utils/docUpload';
import { downloadDocumentSaga } from '../../../utils/downloadDocument';
import {
  del,
  get,
  post,
  putRequest,
} from '../../../utils/request';
import { updateLoans } from '../../Main/actions';
import makeSelectMobile from '../../Mobile/selectors';
import {
  addLoanFailure,
  addLoanSuccess,
  cancelChangeMailingAddressFailure,
  cancelChangeMailingAddressSuccess,
  changeMailingAddressFailure,
  changeMailingAddressSuccess,
  changeMailingAddressValidationFailure,
  changePrimaryLoanFailure,
  changePrimaryLoanSuccess,
  changeStep,
  chooseDocumentCancellation,
  chooseDocumentFailure,
  chooseDocumentSuccess,
  closeModalState,
  downloadDocumentFailure,
  fetchMailingAddressAll,
  fetchMailingAddressAllFailure,
  fetchMailingAddressAllSuccess,
  fetchMailingAddressFailure,
  fetchMailingAddressSuccess,
  processDocument,
  processDocumentFailure,
  processDocumentSuccess,
  setSelectedLoan,
  uploadDocumentFailure,
  uploadDocumentSuccess,
} from './actions';
import {
  ADD_LOAN,
  CANCEL_CHANGE_MAILING_ADDRESS,
  CHANGE_MAILING_ADDRESS,
  CHANGE_MAILING_ADDRESS_ERR_MESSAGE,
  CHANGE_PRIMARY_LOAN,
  CHOOSE_DOCUMENT,
  DOWNLOAD_DOCUMENT,
  FETCH_MAILING_ADDRESS,
  FETCH_MAILING_ADDRESS_ALL,
  FETCH_MAILING_ADDRESS_ERR_MESSAGE,
  PROCESS_DOCUMENT,
  REASON_CODE_DICTIONARY,
  UPLOAD_DOCUMENT,
} from './constants';
import { getChangeValidationFailurePayload } from './helpers';
import makeSelectLoans, { selectFormToUse } from './selectors';

export function* addLoanSaga({ baseUrl, navigationHandler }, { payload }) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    const endpoint = `${baseUrl}/settings/addLoan`;
    const { error, loans, message } = yield call(post, endpoint, payload);
    const { reasons = [] } = error || {};
    if (reasons.length) {
      // There should never be more than one partial error here
      const { message: partialErrorMessage } = REASON_CODE_DICTIONARY[reasons[0]];
      // Use fallback if for some reason error code not accounted for in dict
      yield put(addLoanFailure(partialErrorMessage || INTL_IDS.MESSAGE_ADDLOAN_ERROR));
    }
    if (message) {
      yield put(addLoanSuccess());
      yield put(updateLoans(loans));

      if (isMobile) {
        yield call(navigationHandler, 'AddLoanConfirmation');
      } else {
        yield put(changeStep(2));
      }
    }
  } catch {
    yield put(addLoanFailure(INTL_IDS.MESSAGE_ADDLOAN_ERROR));
  }
}

export function* cancelChangeMailingAddressSaga({ baseUrl, navigationHandler }) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  try {
    const loanNumber = yield select(makeSelectLoans('selectedLoan'));
    const endpoint = `${baseUrl}/settings/mailingaddress/change/${loanNumber}`;
    const { error } = yield call(del, endpoint);
    if (error) {
      yield put(cancelChangeMailingAddressFailure());
      yield put(changeStep(3));
    } else {
      yield put(cancelChangeMailingAddressSuccess());
      yield put(changeStep(4));
      yield put(fetchMailingAddressAll());
    }
  } catch {
    yield put(cancelChangeMailingAddressFailure());
    if (isMobile) {
      yield put(changeStep(3));
    } else {
      yield put(closeModalState());
      yield call(navigationHandler, '/settings/loans/cancelMailing');
    }
  }
}

export function* changeMailingAddressSaga({ baseUrl, navigationHandler }, { payload }) {
  const { requestBody } = payload;
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    const loanNumber = yield select(makeSelectLoans('selectedLoan'));
    const endpoint = `${baseUrl}/settings/mailingaddress/change/${loanNumber}`;
    const { error } = yield call(putRequest, endpoint, requestBody);
    if (error) {
      const { reasons } = error;
      if (reasons.length) {
        const formToUse = yield select(selectFormToUse());
        const valFailurePayload = getChangeValidationFailurePayload(formToUse, reasons);
        if (valFailurePayload) {
          yield put(changeMailingAddressValidationFailure(formToUse, valFailurePayload));
        } else {
          /*
            TODO: determine if we want to handle 26002.2019 (Address change
            request not allowed). We could potentially add logic to set as a
            banner error - but given the UI should never let a user make this
            request if they're not allowed, we can just treat as a full error
            for now. The same goes for any other partial errors not accounted
            for.
          */
          yield put(changeMailingAddressFailure(CHANGE_MAILING_ADDRESS_ERR_MESSAGE));
        }
      }
    } else {
      yield put(changeMailingAddressSuccess());
      if (isMobile) {
        navigationHandler('ChangeMailingAddressConfirmation');
      } else {
        yield put(changeStep(2));
      }
    }
  } catch {
    yield put(changeMailingAddressFailure(CHANGE_MAILING_ADDRESS_ERR_MESSAGE));
  }
}

export function* changePrimaryLoanSaga({ baseUrl }, { payload }) {
  const { loanNumber } = payload;
  try {
    const endpoint = `${baseUrl}/settings/primaryloan/${loanNumber}`;
    const { error } = yield call(putRequest, endpoint);
    if (error) {
      const { reasons } = error;
      if (reasons.length && reasons.includes('3004.2004')) {
        /*
          If user navigates away before request has finished, this could lead
          to a discrepancy between redux state and api's primary loan setting.
          To handle this edge case, we treat 'setting the current primary loan
          as the primary loan' as a success, so the user is not blocked in the
          UI from switching back to the initial primary loan.
        */
        yield put(changePrimaryLoanSuccess({
          loan: loanNumber,
          message: INTL_IDS.MESSAGE_CHANGE_PRIMARYLOAN_SUCCESS,
        }));
      } else {
        yield put(changePrimaryLoanFailure(INTL_IDS.MESSAGE_CHANGE_PRIMARYLOAN_ERROR));
        yield put(setSelectedLoan({ loanNumber: '' }));
      }
    } else {
      yield put(changePrimaryLoanSuccess({
        loan: loanNumber,
        message: INTL_IDS.MESSAGE_CHANGE_PRIMARYLOAN_SUCCESS,
      }));
    }
  } catch {
    yield put(changePrimaryLoanFailure(INTL_IDS.MESSAGE_CHANGE_PRIMARYLOAN_ERROR));
    yield put(setSelectedLoan({ loanNumber: '' }));
  }
}

export function* fetchMailingAddressSaga({ baseUrl }, { payload }) {
  try {
    const { loanNumber } = payload;
    const endpoint = `${baseUrl}/settings/mailingaddress/${loanNumber}`;
    const { addressStatus, error } = yield call(get, endpoint);
    if (error) {
      const { reasons } = error;
      if (reasons.length && !reasons.includes('26000.1000')) {
        /*
          It's not clear if there are other partial errors possible (besides
          26000.1000) for the /settings/address/status GET API endpoint - in
          case there are, we'll treat as a full error since they haven't been
          tested.
        */
        yield put(fetchMailingAddressFailure(FETCH_MAILING_ADDRESS_ERR_MESSAGE));
      } else {
        if (reasons.includes('26000.1000')) {
          addressStatus.canUpload = true;
        } else {
          addressStatus.canUpload = false;
        }
        yield put(fetchMailingAddressSuccess(addressStatus, loanNumber));
      }
    } else {
      addressStatus.canUpload = false;
      yield put(fetchMailingAddressSuccess(addressStatus, loanNumber));
    }
  } catch {
    yield put(fetchMailingAddressFailure(FETCH_MAILING_ADDRESS_ERR_MESSAGE));
  }
}

export function* fetchMailingAddressAllSaga({ baseUrl }) {
  try {
    const {
      allAddressData,
      countryProvinceStateData,
      partialErrorData,
    } = yield call(get, `${baseUrl}/settings/mailingaddress`);
    Object.keys(allAddressData).forEach((loanNumber) => {
      if (partialErrorData[loanNumber]) {
        allAddressData[loanNumber].canUpload =
          partialErrorData[loanNumber].reasons.includes('26000.1000');
        allAddressData[loanNumber].isLoanMancodeY =
          partialErrorData[loanNumber].reasons.includes('26000.1001');
      } else {
        allAddressData[loanNumber].canUpload = false;
      }
    });
    yield put(fetchMailingAddressAllSuccess(allAddressData, countryProvinceStateData));
  } catch {
    yield put(fetchMailingAddressAllFailure(FETCH_MAILING_ADDRESS_ERR_MESSAGE));
  }
}

export default (options) => function* watcherSaga() {
  yield all([
    takeLatest(ADD_LOAN, addLoanSaga, options),
    takeLatest(CANCEL_CHANGE_MAILING_ADDRESS, cancelChangeMailingAddressSaga, options),
    takeLatest(CHANGE_MAILING_ADDRESS, changeMailingAddressSaga, options),
    takeLatest(CHANGE_PRIMARY_LOAN, changePrimaryLoanSaga, options),
    takeLatest(
      CHOOSE_DOCUMENT,
      chooseFileSaga,
      {
        ...options,
        chooseDocumentCancellation,
        chooseDocumentFailure,
        chooseDocumentSuccess,
        processDocument,
      },
    ),
    takeLatest(
      DOWNLOAD_DOCUMENT,
      downloadDocumentSaga,
      {
        ...options,
        downloadDocumentFailure,
      },
    ),
    takeLatest(FETCH_MAILING_ADDRESS, fetchMailingAddressSaga, options),
    takeLatest(FETCH_MAILING_ADDRESS_ALL, fetchMailingAddressAllSaga, options),
    takeLatest(
      PROCESS_DOCUMENT,
      processFileSaga,
      {
        ...options,
        processDocumentFailure,
        processDocumentSuccess,
      },
    ),
    takeLatest(
      UPLOAD_DOCUMENT,
      uploadDocumentJiraSaga,
      {
        ...options,
        uploadDocumentFailure,
        uploadDocumentSuccess,
      },
    ),
  ]);
};
